import { Box, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fund } from '../../api/useFunds';
import { Divider } from '../../components/Divider';
import { toEuroWholeNumber } from '../../utils/numberFormat';
import { calculateCashback } from '../cashbackCalculatorUtil';
import { CashbackModel } from '../../utils/cashbackConstants';
import { AggregatedCommissionBox } from './AggregatedCommissionBox';
import { CashbackComparisonChart } from './CashbackComparisonChart';
import { CashbackEstimationChart } from './CashbackEstimationChart/CashbackEstimationChart';
import { CashbackAdvantageBox } from './CashbackAdvantageBox';

const ChartSectionContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(6, 5),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 2),
  },
}));

const FlexContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(11),

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
}));

const ChartContainer = styled('div')(({ theme }) => ({
  height: '300px',
  width: '600px',
  [theme.breakpoints.only('xs')]: {
    width: '100%',
    alignSelf: 'flex-end',
    marginBottom: '30px',
  },
}));

type ChartSectionProps = {
  fundsMap: Map<Fund, number>;
  bic: Bic;
  model?: CashbackModel;
  bookNowUrl: string;
  onChartBarClick: (bic: Bic) => void;
};

export const ChartSection = ({
  fundsMap,
  bic,
  model,
  bookNowUrl,
  onChartBarClick,
}: ChartSectionProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isLessMd = useMediaQuery(theme.breakpoints.down('md'));
  const [showCashbackAdvanteBox, setShowCashbackAdvantageBox] = useState(false);
  const cashbackAdvantage = calculateCashback(fundsMap, bic, model);
  const showChart = cashbackAdvantage.cashback > 0;

  const handleCalculateNowButtonClick = () => {
    setShowCashbackAdvantageBox(true);
  };

  const handleBackButtonClick = () => {
    setShowCashbackAdvantageBox(false);
  };

  if (showChart && showCashbackAdvanteBox) {
    return (
      <ChartSectionContainer>
        <Typography variant="h6" sx={{ marginBottom: theme.spacing(6.5) }}>
          {t('cashbackCalculator.detailed.cashbackAreaChart.chartHeading', {
            depotVolume: toEuroWholeNumber(cashbackAdvantage.totalInvestedAmount),
          })}
        </Typography>
        <FlexContainer>
          <ChartContainer>
            <CashbackEstimationChart
              wealth={cashbackAdvantage.totalInvestedAmount}
              portfolioCommissionPercent={cashbackAdvantage.totalPortfolioCommissionInPercent}
              model={model}
            />
          </ChartContainer>
          <CashbackAdvantageBox
            bookNowUrl={bookNowUrl}
            cashbackAdvantage={cashbackAdvantage}
            handleBackButtonClick={handleBackButtonClick}
          />
        </FlexContainer>
      </ChartSectionContainer>
    );
  }

  if (isLessMd) {
    return (
      <Box sx={{ padding: theme.spacing(0, 4) }}>
        <AggregatedCommissionBox
          cashbackAdvantage={cashbackAdvantage}
          bookNowUrl={bookNowUrl}
          handleCalculateNowButtonClick={handleCalculateNowButtonClick}
        />
      </Box>
    );
  }

  if (showChart) {
    return (
      <>
        <Divider />
        <ChartSectionContainer>
          <Typography variant="h6" sx={{ marginBottom: theme.spacing(6.5) }}>
            {t('cashbackCalculator.detailed.calculator.chartHeading')}
          </Typography>
          <FlexContainer>
            <ChartContainer>
              <CashbackComparisonChart fundsMap={fundsMap} bic={bic} onClick={onChartBarClick} />
            </ChartContainer>
            <AggregatedCommissionBox
              cashbackAdvantage={cashbackAdvantage}
              bookNowUrl={bookNowUrl}
              handleCalculateNowButtonClick={handleCalculateNowButtonClick}
            />
          </FlexContainer>
        </ChartSectionContainer>
      </>
    );
  }

  return null;
};
